import { DatePicker } from 'antd'
import styled, { css } from 'styled-components'
import { Input } from 'sutra/components/Input/Input'
import { NumberField } from 'sutra/components/NumberField/NumberField'
import { DEFAULT_Z_INDEX as POPOVER_Z_INDEX } from 'sutra/components/Popover/Popover'
import { Select } from 'sutra/components/Select/Select'
import { accents, brand, gray } from 'sutra/tokens/colors'
import { motion } from 'sutra/tokens/motion'
import { radii } from 'sutra/tokens/radii'
import { typography } from 'sutra/tokens/typography'

// Hex mirrors of the Sutra tokens above, for the local antd `ConfigProvider`
// theme in CascadingDateMenu.tsx — antd's color engine (@ant-design/colors /
// @ctrl/tinycolor) can't parse the OKLCH values the token objects hold, so
// the popup's portalled antd DatePicker calendar is themed from these hex
// literals instead. Source of truth: src/sutra/sutra.css.
//
// antd portals the DatePicker to <body> at `zIndexPopupBase + 50`, outside
// the Sutra Popover that hosts this panel. At antd's default base
// (1000 -> 1050) it opens BEHIND the Popover (1060). Pin the base so every
// antd popup opened from the panel sits one level above it.
const ANTD_POPUP_Z_INDEX_OFFSET = 50
export const ANTD_POPUP_THEME = {
  token: {
    zIndexPopupBase: POPOVER_Z_INDEX + 1 - ANTD_POPUP_Z_INDEX_OFFSET,
    colorPrimary: '#28364F',
    colorText: '#28364F',
    colorBorder: '#D5D7DA',
    colorBgContainer: '#FFFFFF',
    borderRadius: 8,
    fontFamily: typography.fontFamily,
    controlItemBgHover: '#EDF1F6',
  },
}

// ── Popup shell ────────────────────────────────────────────────────────────

export const MenuRoot = styled.div`
  display: flex;
  align-items: flex-start;
  background-color: ${brand.white};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  /* Matches FilterSelectionPopover's container so the two popovers read as
   * one surface family. \`6px\` is deliberately off the radii scale (which has
   * no 6) because the filter popover is the reference. */
  border: 1px solid #d8d8d8;
  border-radius: 6px;
  font-family: ${typography.fontFamily};
  overflow: hidden;
`

export const Column = styled.div<{ $width: number; $divided: boolean }>`
  width: ${({ $width }) => $width}px;
  flex-shrink: 0;
  border-right: 1px solid
    ${({ $divided }) => ($divided ? gray[200] : 'transparent')};
`

export const CustomColumn = styled.div`
  flex-shrink: 0;
`

// Column 3 for Prior Period entries — some groups (Week) run to hundreds of
// rows, so this column scrolls instead of growing the popup unboundedly.
// The width follows the widest label (a Week entry reads "September 20 -
// September 26"; MenuRoot clips overflow, so a fixed width cuts it off), with
// 150px as the floor. The stable gutter keeps a classic vertical scrollbar
// from eating into the labels, and overflow-x is hidden because the width
// already fits them.
export const ScrollColumn = styled.div`
  width: max-content;
  min-width: 150px;
  max-height: 320px;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-gutter: stable;
  padding: 4px;
`

export const MenuDivider = styled.div`
  height: 1px;
  margin: 4px 8px;
  background: ${gray[200]};
`

// The "Most recent" toggle row — a MenuRow-like footer row that carries a
// Sutra Checkbox rather than being clickable as a whole (the label click still
// toggles it via the native <label> association).
export const ToggleRow = styled.label<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  font-size: 13px;
  color: ${({ $disabled }) => ($disabled ? gray[400] : brand.stillNight[800])};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  user-select: none;

  &:hover {
    background: ${({ $disabled }) =>
      $disabled ? 'transparent' : accents.reflectionBlue[100]};
  }
`

// ── Menu rows ────────────────────────────────────────────────────────────

export const MenuRow = styled.div<{ $selected: boolean; $active?: boolean }>`
  padding: 6px 10px;
  margin: 2px;
  border-radius: ${radii.xs}px;
  font-size: 13px;
  font-weight: ${({ $selected }) => ($selected ? 600 : 400)};
  color: ${brand.stillNight[800]};
  background: ${({ $selected, $active }) =>
    ($active ?? $selected) ? accents.reflectionBlue[100] : 'transparent'};
  cursor: pointer;
  user-select: none;
  white-space: nowrap;

  &:hover {
    background: ${accents.reflectionBlue[100]};
  }
`

// The two rows that open column 2 ("X to Date" / "Custom"). Pass `$active`
// from the `hoveredItem` state (not a bare CSS `:hover`) — a CSS hover would
// un-highlight the row the instant the pointer crosses into column 2,
// flickering while the submenu is still open. `$selected` stays true
// selection only, so the row bolds only when its preset is actually applied.
export const MenuRowWithArrow = styled(MenuRow)`
  display: flex;
  justify-content: space-between;
  align-items: center;

  & .anticon {
    font-size: 12px;
    color: ${gray[500]};
  }
`

// ── Custom panels — Fixed tab (dual calendar) ──────────────────────────────

export const FixedPanel = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 12px;
  gap: 12px;
`

export const CalendarsRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`

export const CalendarColumn = styled.div`
  width: 236px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

export const CalendarLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${gray[600]};
`

export const DateTextInput = styled(Input)`
  width: 236px;
`

export const MonthNavRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
`

// Sutra's Select portals its panel to <body> too, at its own `zIndex` prop;
// its default sits below the Popover that hosts this panel.
const PopoverSelect = styled(Select).attrs({ zIndex: POPOVER_Z_INDEX + 1 })`
  && {
    padding: 0 8px;
    gap: 4px;
  }
`

export const MonthSelect = styled(PopoverSelect)`
  width: 110px;
`

export const YearSelect = styled(PopoverSelect)`
  width: 70px;
`

export const CalendarGrid = styled.div`
  margin-top: 8px;
`

export const WeekdayRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 32px);
  gap: 2px;
  margin-bottom: 4px;
`

export const WeekdayCell = styled.div`
  text-align: center;
  font-size: 11px;
  color: ${gray[400]};
  font-weight: 500;
  padding: 4px 0;
`

export const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 32px);
  gap: 2px;
`

export const DayCellSpacer = styled.div`
  height: 32px;
`

export const DayCell = styled.button<{
  $endpoint: boolean
  $inRange: boolean
  $today: boolean
  $future: boolean
}>`
  height: 32px;
  width: 32px;
  border: none;
  border-radius: ${({ $endpoint }) => ($endpoint ? '50%' : '0')};
  background-color: ${({ $endpoint, $inRange }) =>
    $endpoint
      ? brand.stillNight[800]
      : $inRange
        ? accents.reflectionBlue[50]
        : 'transparent'};
  color: ${({ $future, $endpoint, $today }) =>
    $future
      ? gray[300]
      : $endpoint
        ? brand.white
        : $today
          ? accents.reflectionBlue[500]
          : brand.stillNight[800]};
  font-size: 13px;
  font-family: ${typography.fontFamily};
  cursor: ${({ $future }) => ($future ? 'not-allowed' : 'pointer')};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${({ $today }) => ($today ? 600 : 400)};
  transition:
    background-color ${motion.duration.fast}ms ${motion.ease},
    color ${motion.duration.fast}ms ${motion.ease};
  opacity: ${({ $future }) => ($future ? 0.5 : 1)};

  &:hover {
    ${({ $endpoint, $inRange, $future }) =>
      !$endpoint && !$inRange && !$future
        ? css`
            background-color: ${gray[100]};
          `
        : ''}
  }
`

export const RangeArrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 100px;
  font-size: 18px;
  color: ${gray[400]};
`

// ── Custom panels — Since / Last tabs ──────────────────────────────────────

export const SincePanel = styled.div`
  padding: 16px;
`

export const SinceDatePicker = styled(DatePicker)`
  width: 100%;
  margin-bottom: 16px;
`

export const LastPanel = styled.div`
  padding: 16px;
`

export const LastControlsRow = styled.div`
  margin-bottom: 16px;
  display: flex;
  gap: 8px;
  align-items: center;
`

export const LastValueInput = styled(NumberField)`
  width: 80px;
`

export const LastUnitSelect = styled(PopoverSelect)`
  width: 100px;
`
